import { useEffect, useMemo, useState } from 'react'
import { Button, Divider, Dropdown, Grid, Header, Icon, Label, Loader, Segment, Statistic, Table } from 'semantic-ui-react'
import moment from 'moment'
import 'moment/locale/es'
import toast from 'react-hot-toast'
import { connect } from 'react-redux'
import superAdminBillingActions from '../../store/actions/super_admin_billing'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const PartnerBillingPage = ({ getSuperAdminBillingsByPeriod }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [billingPeriods, setBillingPeriods] = useState(() => {
    const currentDate = moment();
    const periodsArray = [
      {
        key: currentDate.format('YYYY-MM'),
        text: currentDate.format('MMMM YYYY').charAt(0).toUpperCase() + currentDate.format('MMMM YYYY').slice(1),
        value: currentDate.format('YYYY-MM')
      }
    ];
    for (let i = 1; i <= 12; i++) {
      const pastDate = currentDate.clone().subtract(i, 'months');
      periodsArray.push({
        key: pastDate.format('YYYY-MM'),
        text: pastDate.format('MMMM YYYY').charAt(0).toUpperCase() + pastDate.format('MMMM YYYY').slice(1),
        value: pastDate.format('YYYY-MM')
      });
    }
    return periodsArray;
  });
  const [currentBillingPeriod, setCurrentBillingPeriod] = useState(() => billingPeriods[0]?.value);
  const [isFetching, setIsFetching] = useState(false);
  const [billingData, setBillingData] = useState([]);

  useEffect(() => {
    const fetchBillingData = async () => {
      setIsFetching(true);
      try {
        const superAdminBillingData = await getSuperAdminBillingsByPeriod(currentBillingPeriod);
        setBillingData(superAdminBillingData);
      } finally {
        setIsFetching(false);
      }
    }
    fetchBillingData();
  }, [currentBillingPeriod]);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$ —'
    return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
  }

  const statistics = useMemo(() => {
    const withBilling = billingData.filter(item => item.billingData?.amount)
    const withoutBilling = billingData.filter(item => !item.billingData?.amount)
    const total = withBilling.reduce((sum, item) => sum + Number(item.billingData.amount || 0), 0)

    return {
      totalPartners: billingData.length,
      withBilling: withBilling.length,
      withoutBilling: withoutBilling.length,
      totalAmount: total
    }
  }, [billingData])

  return (
    <div className='erp-partner-billing-page' style={{ padding: 20 }}>
      <Header as='h1' size='large' style={{ marginBottom: 20 }}>
        <Icon name='dollar sign' color='green' />
        <Header.Content>
          {t('Facturación de Socios')}
          <Header.Subheader>
            {t('Gestión y seguimiento de facturación mensual')}
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Grid>
          <Grid.Row>
            <Grid.Column width={5}>
              <div>
                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{t('Período de Facturación')}</p>
                <Dropdown
                  placeholder={t('Selecciona un período')}
                  fluid
                  selection
                  options={billingPeriods}
                  search
                  allowAdditions
                  value={currentBillingPeriod}
                  onChange={(e, { value }) => setCurrentBillingPeriod(value)}
                  onAddItem={(e, { value }) => {
                    const normalizedText = value.trim().toLowerCase();
                    const date = moment(normalizedText, 'MMMM YYYY', 'es', true);
                    const hasBillingPeriod = billingPeriods.some(period => period.value === date.format('YYYY-MM'));
                    if (!hasBillingPeriod) {
                      if (date.isValid()) {
                        const newPeriod = {
                          key: date.format('YYYY-MM'),
                          text: date.format('MMMM YYYY').charAt(0).toUpperCase() + date.format('MMMM YYYY').slice(1),
                          value: date.format('YYYY-MM')
                        };
                        setBillingPeriods((prevPeriods) => [newPeriod, ...prevPeriods]);
                        setCurrentBillingPeriod(newPeriod.value);
                      } else {
                        toast.error(t('Formato inválido. Por favor ingresa el período en formato "Mes Año", por ejemplo, "Marzo 2023".'));
                      }
                    } else {
                      setCurrentBillingPeriod(date.format('YYYY-MM'));
                    }
                  }}
                  additionLabel={t('Agregar período: ')}
                  noResultsMessage={t('No se encontraron períodos.')}
                  loading={isFetching}
                  disabled={isFetching}
                />
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      {(isFetching && billingData.length === 0) ? (
        <Segment>
          <Loader active inline='centered'>
            {t('Cargando datos de facturación...')}
          </Loader>
        </Segment>
      ) : (
        <>
          <Grid columns={4} stackable>
            <Grid.Column>
              <Segment textAlign='center' color='blue'>
                <Statistic size='small'>
                  <Statistic.Value>
                    <Icon name='users' />
                    {statistics.totalPartners}
                  </Statistic.Value>
                  <Statistic.Label>{t('Total de Socios')}</Statistic.Label>
                </Statistic>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment textAlign='center' color='green'>
                <Statistic size='small'>
                  <Statistic.Value>
                    <Icon name='check circle' />
                    {statistics.withBilling}
                  </Statistic.Value>
                  <Statistic.Label>{t('Con Facturación')}</Statistic.Label>
                </Statistic>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment textAlign='center' color='orange'>
                <Statistic size='small'>
                  <Statistic.Value>
                    <Icon name='warning circle' />
                    {statistics.withoutBilling}
                  </Statistic.Value>
                  <Statistic.Label>{t('Sin Facturación')}</Statistic.Label>
                </Statistic>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment textAlign='center' color='teal'>
                <Statistic size='small'>
                  <Statistic.Value style={{ fontSize: '1.5rem' }}>
                    {formatCurrency(statistics.totalAmount)}
                  </Statistic.Value>
                  <Statistic.Label>{t('Total a Facturar')}</Statistic.Label>
                </Statistic>
              </Segment>
            </Grid.Column>
          </Grid>

          <Segment>
            <Header as='h3' size='small'>
              <Icon name='list' />
              {t('Lista de Socios')}
            </Header>
            <Divider />
            <Table striped selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={7}>
                    <Icon name='user' /> {t('Socio')}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={3} textAlign='center'>
                    <Icon name='info circle' /> {t('Estado')}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={3} textAlign='right'>
                    <Icon name='dollar sign' /> {t('Total a Pagar')}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2} textAlign='center'>
                    <Icon name='setting' /> {t('Acciones')}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {billingData.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan='5' textAlign='center'>
                      <div style={{ padding: '20px 0' }}>
                        <Icon name='inbox' size='huge' color='grey' />
                        <p style={{ marginTop: 10, color: '#999' }}>
                          {t('No hay socios registrados en este período')}
                        </p>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  billingData.map((billingItem, index) => (
                    <Table.Row
                      key={billingItem.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => history.push(`/partner_billing/${billingItem.id}`)}
                    >
                      <Table.Cell>
                        <Header as='h4' style={{ margin: 0 }}>
                          <Icon name='building' color='grey' />
                          <Header.Content>
                            {billingItem.name}
                          </Header.Content>
                        </Header>
                      </Table.Cell>
                      <Table.Cell textAlign='center'>
                        {billingItem.billingData?.amount ? (
                          <Label color='green' size='small'>
                            <Icon name='check circle' />
                            {t('Facturado')}
                          </Label>
                        ) : (
                          <Label color='orange' size='small'>
                            <Icon name='warning circle' />
                            {t('Sin facturar')}
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        <strong style={{ fontSize: '1.1em', color: billingItem.billingData?.amount ? '#21ba45' : '#999' }}>
                          {formatCurrency(billingItem.billingData?.amount)}
                        </strong>
                      </Table.Cell>
                      <Table.Cell textAlign='center'>
                        <Button
                          icon
                          size='small'
                          color='blue'
                          basic
                          onClick={(e) => {
                            e.stopPropagation()
                            history.push(`/partner_billing/${billingItem.id}`)
                          }}
                        >
                          <Icon name='eye' /> {t('Ver Detalles')}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
          </Segment>
        </>
      )}


    </div>
  )
}

const mapDispatchToProps = (dispatch) => ({
  getSuperAdminBillingsByPeriod: (period) => dispatch(superAdminBillingActions.getSuperAdminBillingsByPeriod(period))
})

export default connect(null, mapDispatchToProps)(PartnerBillingPage)
